import React, { useLayoutEffect, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { FloatingButtons } from '../components/FloatingButtons';
import { ChatDrawer } from '../components/ChatDrawer';
import { ReadingProgress } from '../components/ReadingProgress';
import { MobileBottomCTA } from '../components/MobileBottomCTA';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { useScrollAnimation } from '../utils/AnimationObserver';
import { Brain, ArrowLeft } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
const fadeUpVariant = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
function VimeoEmbed({
  videoId,
  caption,
  aspect = 56.25
}: {
  videoId: string;
  caption?: string;
  aspect?: number;
}) {
  return <div className="bg-white rounded-2xl overflow-hidden border border-slate-200" data-id="element-620">
      <div style={{
      padding: `${aspect}% 0 0 0`,
      position: 'relative'
    }} data-id="element-621">
        <iframe src={`https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0&app_id=58479`} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
      }} title={caption || 'Video'} data-id="element-622" />
      </div>
      {caption && <p className="text-xs uppercase tracking-[0.2em] text-slate-500 px-6 py-4 text-center bg-slate-50 border-t border-slate-100" style={{
      fontFamily: "'Ronzino', sans-serif"
    }} data-id="element-623">
          {caption}
        </p>}
    </div>;
}
function ProofImage({
  src,
  alt,
  caption
}: {
  src: string;
  alt: string;
  caption?: string;
}) {
  return <div className="bg-slate-50 rounded-2xl overflow-hidden border border-slate-200" data-id="element-624">
      <img src={src} alt={alt} loading="lazy" decoding="async" className="w-full h-auto" data-id="element-625" />
      {caption && <p className="text-xs uppercase tracking-[0.2em] text-slate-500 px-6 py-4 text-center bg-slate-50 border-t border-slate-100" style={{
      fontFamily: "'Ronzino', sans-serif"
    }} data-id="element-626">
          {caption}
        </p>}
    </div>;
}
const processSteps = [{
  number: '01',
  title: 'Contextual inquiry',
  desc: '16 interviews (8 staff, 8 faculty) across colleges, run in two-week sprints. Video-recorded every session, auto-tagged with Dovetail AI, and revisited participants as designs evolved.'
}, {
  number: '02',
  title: 'Affinity mapping',
  desc: 'Organized findings into four critical pain points: terminology confusion, navigation issues, poor visual hierarchy, and time-consuming manual entry.'
}, {
  number: '03',
  title: 'Navigation + design system',
  desc: 'Redesigned the navigation and consolidated four mismatched UI libraries into one system, while establishing UX best practices for the developer workflow.'
}, {
  number: '04',
  title: 'Rapid prototyping',
  desc: 'Used AI tools like UX Pilot to generate and test navigation concepts fast, refining live with engineers and stakeholders in the same week.'
}, {
  number: '05',
  title: 'AI parser POC',
  desc: 'On a parallel track, worked with Python engineers to build a working CV parser. Wrote prompt logic, tested against 36-page CVs, and tuned for accuracy.'
}, {
  number: '06',
  title: 'Validation + pilot handoff',
  desc: 'Ran stakeholder sign-off and usability testing, saw the system go live in production, then handed the working prototype to Amazon for a 2026 rollout pilot.'
}];
export function CaseStudyCaseWestern({
  isOverlay = false
}: {
  isOverlay?: boolean;
}) {
  const navigate = useNavigate();
  const {
    viewPreference
  } = usePortfolio();
  useCaseStudyNavigation();
  useScrollAnimation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  return <>
      <ReadingProgress color="#003071" data-id="element-627" />
      {!isOverlay && <CustomCursor data-id="element-628" />}

      <div className="flex min-h-screen bg-white text-slate-900" data-id="element-629">
        <div className="flex-1 min-w-0 flex flex-col relative pb-20 lg:pb-0" data-id="element-630">
          {!isOverlay && <CaseStudyHeader data-id="element-631" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-632" />}

          <main className="flex-1 w-full pb-32" data-id="element-633">
            {/* HEADER BLOCK */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="pt-32 sm:pt-40 pb-16 sm:pb-24 px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full" data-id="element-634">
              <button type="button" onClick={() => navigate(viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional')} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-slate-900 hover:text-slate-500 transition-colors mb-10 group" style={{
              fontFamily: "'Ronzino', sans-serif"
            }} data-id="element-635">
                <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" data-id="element-636" />
                Back to home
              </button>

              <div className="mb-8" data-id="element-637">
                <Brain className="w-8 h-8 text-[#003071]" weight="light" data-id="element-638" />
              </div>

              <h1 className="text-3xl sm:text-5xl md:text-6xl font-light text-[#003071] leading-[1.1] tracking-tight mb-4" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-639">
                Faculty Information System
              </h1>

              <p className="text-lg sm:text-xl text-slate-500 font-light mb-8" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-640">
                Leading the UX/UI redesign and AI exploration of a
                university-wide faculty data platform.
              </p>

              <p className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl" style={{
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }} data-id="element-641">
                I led the redesign of Case Western Reserve University's Faculty
                Information System, the central platform for tracking faculty
                academic activity. What began as a 3-month contract extended to
                six as we tackled parallel workstreams: redesigning navigation,
                consolidating the design system, setting UX best practices for
                the developer workflow, and exploring AI to ease the heavy
                manual data entry. The AI CV parser was one outcome of that
                work, not the whole story.
              </p>
            </motion.section>

            {/* HERO IMAGE CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-16 sm:mb-24" data-id="element-642">
              <div className="bg-slate-100 rounded-2xl py-10 sm:py-14 md:py-16 px-6 sm:px-10 md:px-14 flex items-center justify-center" data-id="element-643">
                <img src="https://cdn.magicpatterns.com/uploads/r6QmYhNhTgdAoSYhnocL3u/CWRU_COMPV.png" alt="Faculty Information System Interface" className="w-full max-w-none object-contain drop-shadow-xl" data-id="element-644" />
              </div>
            </motion.section>

            {/* META STRIP */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-645">
              <div className="border-t border-b border-slate-200 py-10" data-id="element-646">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12 mb-12" data-id="element-647">
                  <div data-id="element-648">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#003071] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-649">
                      Product
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-650">
                      Faculty Information System
                    </div>
                  </div>
                  <div data-id="element-651">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#003071] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-652">
                      My role
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-653">
                      Lead Product Designer
                    </div>
                  </div>
                  <div data-id="element-654">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#003071] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-655">
                      Timeline
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-656">
                      Jan 2025 to Jun 2025
                    </div>
                  </div>
                  <div data-id="element-657">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#003071] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-658">
                      Skills
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-659">
                      Product design, AI integration, User research, Prototyping
                    </div>
                  </div>
                </div>
                <div className="pt-10 border-t border-slate-200" data-id="element-660">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#003071] mb-2" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-661">
                    Team
                  </div>
                  <div className="text-base font-medium text-slate-900" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-662">
                    Mateo Cohen (Design Lead), CWRU Innovation Group,
                    Engineering Partners
                  </div>
                </div>
              </div>
            </motion.section>

            {/* QUOTE-OVERLAY IMAGE CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-663">
              <div className="relative rounded-2xl min-h-[280px] sm:h-[480px] sm:aspect-auto overflow-hidden flex items-center justify-center p-6 sm:p-16" data-id="element-664">
                <img src="https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=1600&q=80" alt="Faculty reviewing a printed CV at a desk" className="absolute inset-0 w-full h-full object-cover" data-id="element-665" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/40 to-slate-900/20" data-id="element-666"></div>

                <div className="relative z-10 text-center max-w-3xl mx-auto" data-id="element-667">
                  <p className="text-lg sm:text-3xl md:text-4xl font-light italic text-white leading-snug" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-668">
                    "Reviewing a single CV could eat 30 minutes I didn't have."
                  </p>
                  <div className="text-xs tracking-[0.2em] uppercase text-white/70 mt-6" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-669">
                    Faculty admin, user interview
                  </div>
                </div>
              </div>
            </motion.section>

            {/* CONTENT SECTIONS */}
            <section className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full space-y-24 sm:space-y-32" data-id="element-670">
              {/* Section A: Problem */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-671">
                <div className="flex items-center gap-3 mb-4" data-id="element-672">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-673">
                    01
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-674" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-675">
                  Problem: Seven years of usability debt
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-676">
                  The FIS had evolved over roughly seven years with no dedicated
                  UX expertise, accruing usability debt across a diverse,
                  multi-college user base. Faculty spent 3+ hours on each annual
                  review, navigation made information hard to find,
                  accessibility gaps caused confusion, and excessive manual data
                  entry was the loudest complaint of all.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-677">
                  The sharpest example of that manual burden was CV review.
                  Administrators spent roughly 30 minutes per faculty CV,
                  reading inconsistent formats and hand-extracting publications,
                  grants, and credentials into spreadsheets. With 200+ faculty
                  per cycle, that is 100+ hours of manual work blocking grant
                  timelines, accreditation, and internal reviews.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-678">
                  And it wasn&rsquo;t work the institution could simply hire its
                  way out of. Faculty CVs aren&rsquo;t resumes. They&rsquo;re
                  20- to 40-page artifacts where formatting varies by
                  discipline, the same publication can appear three different
                  ways across two pages, and missing a single grant can hold up
                  an entire review. A generic OCR pipeline would surface text.
                  It wouldn&rsquo;t surface the right text in the right
                  structure, and it certainly wouldn&rsquo;t earn the trust of
                  the people signing off on the data.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-679">
                  It wasn&rsquo;t a reading problem. It was a data-extraction
                  problem dressed up as a reading problem.
                </p>

                <div className="bg-slate-100 rounded-lg p-6 sm:p-8 my-10 overflow-x-auto" data-id="element-680">
                  <div className="text-xl sm:text-2xl font-light text-[#003071] sm:whitespace-nowrap" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-681">
                    20- to 40-page CVs. 7 colleges. No standardization.
                  </div>
                </div>

                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-682">
                  And the legacy interface they were stuck with looked like
                  this:
                </p>

                <ProofImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/vRuKFVtSu1k26mpfBtXnY7/Before.png" alt="Legacy Faculty Information System interface before redesign" caption="Before: the legacy FIS interface" data-id="element-683" />
              </motion.div>

              {/* Section B: Research */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-684">
                <div className="flex items-center gap-3 mb-4" data-id="element-685">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-686">
                    02
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-687" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-688">
                  Research: 16 interviews, four clear pain points
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-689">
                  I ran contextual inquiry with 16 participants (8 staff, 8
                  faculty) across colleges in iterative two-week sprints. Every
                  session was video-recorded, auto-tagged with Dovetail AI, and
                  organized through affinity diagramming, and I revisited
                  participants as designs evolved. Four critical pain points
                  surfaced: terminology confusion across schools, navigation
                  issues, poor visual hierarchy, and time-consuming manual
                  entry.
                </p>

                <VimeoEmbed videoId="1142194486" caption="User interviews capturing sentiment and pain points" data-id="element-690" />
              </motion.div>

              {/* Process Timeline */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-691">
                <div className="flex items-center gap-3 mb-4" data-id="element-692">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-693">
                    03
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-694" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-695">
                  Process: Six steps across every workstream
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-696">
                  Six months of work across research, navigation, the design
                  system, and a parallel AI track, telescoped into the six
                  decisions that moved the project forward.
                </p>

                <div data-id="element-697">
                  {processSteps.map((step, idx) => <div key={step.number} className="relative flex gap-5 sm:gap-8 pb-12 sm:pb-16 last:pb-0" data-id="element-698">
                      {idx !== processSteps.length - 1 && <div className="absolute left-5 sm:left-6 top-10 sm:top-12 bottom-0 w-px bg-slate-200" aria-hidden="true" data-id="element-699" />}
                      {/* Number */}
                      <div className="relative z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white border border-slate-300 flex items-center justify-center flex-shrink-0" data-id="element-700">
                        <span className="text-xs sm:text-sm font-medium text-slate-900" style={{
                      fontFamily: "'Ronzino', sans-serif"
                    }} data-id="element-701">
                          {step.number}
                        </span>
                      </div>
                      {/* Content */}
                      <div className="flex-1 pt-1.5 sm:pt-2.5" data-id="element-702">
                        <h4 className="text-lg sm:text-xl font-medium text-[#003071] mb-2" style={{
                      fontFamily: "'Gefika', sans-serif"
                    }} data-id="element-703">
                          {step.title}
                        </h4>
                        <p className="text-base text-slate-600 leading-relaxed max-w-2xl" style={{
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }} data-id="element-704">
                          {step.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </motion.div>

              {/* Section C: Solution */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-705">
                <div className="flex items-center gap-3 mb-4" data-id="element-706">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-707">
                    04
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-708" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-709">
                  Solution: Redesign first, AI in parallel
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-710">
                  The core redesign delivered three improvements: standardized
                  terminology across schools, a clearer visual hierarchy, and
                  redesigned navigation, all built on a single consolidated
                  design system in place of four mismatched libraries. Many
                  faculty were skeptical of AI and simply wanted faster manual
                  workflows, so the UX work came first.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-711">
                  On a parallel track, I designed an AI pipeline that ingests a
                  CV, extracts structured fields (publications, grants, courses,
                  dates), and surfaces them in a single review panel where
                  admins scan, edit inline, and push to the database. The key
                  call was to keep a human in the loop on every field. Trust
                  compounds, and black-box automation breaks the moment it is
                  wrong once.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-10" data-id="element-712">
                  <ProofImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/5KpMMuX7A152SY5E7yEEkP/Ideation_1.png" alt="Navigation iteration 1" caption="Iteration 01" data-id="element-713" />
                  <ProofImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/fZ3J5Y9Kes9FYqDKGquNRG/Ideation_2.png" alt="Navigation iteration 2" caption="Iteration 02" data-id="element-714" />
                  <ProofImage src="https://uploadthingy.s3.us-west-1.amazonaws.com/sAd42ZLH2mUmRLh4anJCPC/Ideation_3.png" alt="Navigation iteration 3" caption="Iteration 03 (final)" data-id="element-715" />
                </div>

                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-716">
                  And here&rsquo;s the shipped home view:
                </p>

                <ProofImage src="https://cdn.magicpatterns.com/uploads/b1CFgyJEco99E9Ys6agrnZ/Home_Page_FIS.png" alt="Faculty Information System home page interface" caption="Shipped: FIS home page" data-id="element-717" />
              </motion.div>

              {/* Engineering collaboration */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-718">
                <div className="flex items-center gap-3 mb-4" data-id="element-719">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-720">
                    05
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-721" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-722">
                  Build: Design and engineering, same table
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-723">
                  Across both the redesign and the AI track, I ran live working
                  sessions with Python engineers and stakeholders, translating
                  technical constraints into design opportunities in the room.
                  Faster than handoffs. Less rework.
                </p>

                <VimeoEmbed videoId="1142229645" caption="Collaborative design sessions with engineers and stakeholders" data-id="element-724" />
              </motion.div>

              {/* Live prototype demo */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-725">
                <div className="flex items-center gap-3 mb-4" data-id="element-726">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-727">
                    06
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-728" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-729">
                  Demo: The AI track in action
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-730">
                  One workstream from the redesign: watch a 36-page CV get
                  parsed in seconds. Publications, grants, courses, and dates,
                  all into editable fields ready for the faculty database.
                </p>

                <VimeoEmbed videoId="1148647481" caption="AI-powered CV parsing, end to end" aspect={52.31} data-id="element-731" />

                {/* Designer's note on the demo */}
                <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6 sm:p-10 mt-10" data-id="element-732">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#003071] mb-4" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-733">
                    A note on the project
                  </div>
                  <h4 className="text-xl sm:text-2xl font-light text-slate-900 mb-5 leading-snug" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-734">
                    Reflections on a six-month build race
                  </h4>
                  <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-5" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-735">
                    Looking back at the task list and timeline, I was helping
                    with product management, owning every design task, and
                    vibe-coding alongside engineering while studying Python. It
                    taught me a lot about management and about how to delegate
                    to AI when no one had the &ldquo;right&rdquo; way to do any
                    of this AI plus design work yet. The designs read as AI in
                    the demo because we were the real testing team for these
                    models; we leaned on them for more than half of the work to
                    get through 150-plus screens and countless components.
                    Outside my own project, I built skills, worked on
                    automations, and spoke with plenty of AI tool founders to
                    give feedback from the trenches. That was my daily workflow
                    with AI: a true wild west, and I wouldn&rsquo;t have it any
                    other way.
                  </p>
                  <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-736">
                    So no, we didn&rsquo;t refactor every section in six months,
                    but on a platform that had gone seven years without a design
                    hand, the progress was real: a more accessible, easier-to-
                    understand experience driven by direct user feedback and the
                    pain points from our research. I led with function to get
                    the most out of the timeline, then left detailed notes and a
                    repeatable process so the remaining tech team could keep
                    moving the UI and user experience forward.
                  </p>
                </div>
              </motion.div>

              {/* Section D: Outcome */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-737">
                <div className="flex items-center gap-3 mb-4" data-id="element-738">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#003071'
                }} data-id="element-739">
                    07
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#003071'
                }} aria-hidden="true" data-id="element-740" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#003071] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-741">
                  Outcome: Shipped and measured
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-8 mb-8 border-y border-slate-100" data-id="element-742">
                  <div data-id="element-743">
                    <div className="text-4xl sm:text-5xl font-light text-[#003071] mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-744">
                      85%
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#003071]" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-745">
                      First-pass parsing accuracy
                    </div>
                  </div>
                  <div data-id="element-746">
                    <div className="text-4xl sm:text-5xl font-light text-[#003071] mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-747">
                      75%
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#003071]" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-748">
                      Reduction in admin review time
                    </div>
                  </div>
                </div>

                <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-749">
                  Beyond the metrics, the redesign delivered standardized
                  terminology, a clearer visual hierarchy, and improved
                  navigation, and the system went live in production during my
                  final months. The AI track proved compelling enough that we
                  secured a two-month Amazon pilot for real-world testing,
                  staged for 2026 implementation, with the methodology being
                  prepped for an Amazon research white paper.
                </p>
              </motion.div>
            </section>

            <CaseStudyNavigation accent="#003071" nextCase={{
            category: 'Financial Services',
            title: 'US Bank Commercial Card',
            slug: 'us-bank'
          }} data-id="element-750" />
          </main>
        </div>
      </div>

      <FloatingButtons data-id="element-751" />
      <ChatDrawer data-id="element-752" />
      <MobileBottomCTA data-id="element-753" />
      {!isOverlay && <Footer data-id="element-754" />}
    </>;
}